/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Where a value came from, and — for a detected mapping — the way back** (`#1001`).
 *
 * `InheritanceMark`'s counterpart for the other relation that fills a row for the operator: a
 * detector. Todd's framing on `#1001` is the reason it exists — *"Revert is not about the mappings we
 * know are wrong; it is about the ones we have not found yet"* — so it cannot depend on knowing which
 * guesses were bad. It offers the way back on every detected mapping that still holds its guess, and
 * asks for a look on the ones whose match was not exact.
 *
 * Three things it deliberately does not do:
 *
 * - **It does not phrase anything.** The sentences are `provenance-presentation.ts`'s, beside the
 *   chip labels, so the legend and the row cannot describe one mark two ways.
 * - **It does not decide what reverting does to a row.** That is the surface's `onRevert` — a
 *   parameter row and a body leaf are different shapes — and `null` is a surface saying it has no
 *   detected mappings to revert, which the type makes it say out loud.
 * - **It does not keep a reason only in a hover.** Both explanations go through `ControlHover`,
 *   which also renders them for the accessible tree (`#1276`).
 */

import React from 'react';
import { Box, Button, HStack } from '@chakra-ui/react';

import { ControlHover } from '../../ui/ControlHover';
import { revertValueOf, type ValueOrigin } from './parameter-editor.adapters';
import { ProvenanceChip } from './ProvenanceChip';
import {
  UNCONFIRMED_GUESS_LABEL,
  describeRevertDetection,
  describeRevertToTemplate,
  describeInferredDeclaration,
  describeUnconfirmedGuess,
} from './provenance-presentation';
import { EvidenceMark } from './EvidenceMark';
import type { TemplateRevert } from './MappingRow';

export interface ProvenanceMarkProps {
  origin: ValueOrigin | undefined;
  /**
   * Put the sample's literal back in place of the detected mapping. **Required and nullable**
   * (`#1134`): `null` is a surface that holds no detected mappings, and a surface that forgot cannot
   * compile.
   */
  onRevert: (() => void) | null;
  /**
   * Put back the template's current row, on a row changed from it (Todd, 2026-09-24). **Required and
   * nullable**, for the reason `onRevert` is: `null` where the row came from no template, the template
   * no longer has it, or the viewer cannot see it.
   */
  templateRevert: TemplateRevert | null;
}

export const ProvenanceMark: React.FC<ProvenanceMarkProps> = ({ origin, onRevert, templateRevert }) => {
  if (!origin) return null;

  const guess = describeUnconfirmedGuess(origin);
  const inferred = describeInferredDeclaration(origin);
  const revertValue = revertValueOf(origin);
  // Two ways back, never both: a detected guess reverts to its sample, a row changed from a template
  // to the template's row. Each asks its own question of the origin, and no origin answers both.
  const revert =
    onRevert !== null && revertValue !== undefined
      ? { title: describeRevertDetection(revertValue), act: onRevert }
      : templateRevert && origin.kind === 'overridden'
        ? { title: describeRevertToTemplate(templateRevert.templateName), act: templateRevert.onRevert }
        : null;

  return (
    <HStack gap={1} align="center" display="inline-flex" flexShrink={0}>
      <ProvenanceChip provenance={origin.kind} />
      {guess && (
        <ControlHover text={guess}>
          <Box
            as="span"
            tabIndex={0}
            fontSize="10px"
            px={1.5}
            borderRadius="sm"
            bg="warning.tint"
            color="warning.ink"
            whiteSpace="nowrap"
            data-mark="unconfirmed-guess"
          >
            {UNCONFIRMED_GUESS_LABEL}
          </Box>
        </ControlHover>
      )}
      {inferred && (
        <ControlHover text={inferred}>
          <EvidenceMark focusable />
        </ControlHover>
      )}
      {revert && (
        <ControlHover text={revert.title}>
          <Button
            size="xs"
            variant="ghost"
            height="16px"
            minW="auto"
            px={1}
            fontSize="10px"
            fontWeight="500"
            color="fg.muted"
            _hover={{ color: 'accent.solid' }}
            onClick={(e) => {
              // The row header is clickable; reverting must not also open the row it just changed.
              e.stopPropagation();
              revert.act();
            }}
          >
            revert
          </Button>
        </ControlHover>
      )}
    </HStack>
  );
};
